/**
 * evaluate.ts — Pure rule evaluation engine.
 *
 * Public API:
 *   evaluateGroup(group, ctx): boolean   — recursive ALL/ANY evaluation
 *   evaluateCondition(cond, ctx): boolean — single condition
 *   evaluate(rule, ctx): boolean          — full rule evaluation
 *
 * Fail-open: any structural error (unknown source, bad operator) → false, not throw.
 * GEO_COUNTRY=null → GEO condition → false, UNLESS the author explicitly set
 * condition.degradation_fallback='true' (WEB-2174), in which case it → true.
 * Default (field absent) stays false — do not change this without re-reading
 * WEB-2174 (owner decision 16.09.2026: default preserved on purpose).
 *
 * D1 RATIFIED: ANSWER source reads context.answers[blockId] = option.id (NOT label).
 *
 * WEB-1367: прежняя пометка «F1d SEAM: select_dropdown хранит label вместо id»
 * описывала шов, которого НЕ существует — у select_dropdown нет пар id/label
 * вовсе (selectOptions: плоский массив строк, block-schema/content.ts);
 * сравнивается ровно то, что хранится. Нормализовать нечего.
 *
 * Pure module: no React/DOM/navigator/payment-SDK imports.
 */

import type { Condition, Group, Rule } from './rule.schema';
import type { EvalContext } from './types';
import { applyOperator } from './operators';

// ---------------------------------------------------------------------------
// Condition readiness (WEB-1255 audit 22.08 follow-up)
// ---------------------------------------------------------------------------

/**
 * Sources whose source_ref is mandatory to mean anything (block id / param
 * name / variable name). Single source of truth — ConditionRow.tsx imports
 * `isSourceRefRequired` below rather than keeping its own copy of this list
 * (fixed in pack 2 FB2, was a duplicated literal drifting from this one).
 */
const SOURCES_REQUIRING_REF: ReadonlySet<Condition['source']> = new Set([
  'ANSWER',
  'URL_PARAM',
  'VARIABLE',
]);

/**
 * Operators that compare the resolved source against a literal string, where
 * an EMPTY literal is never a meaningful comparison target. Left OUT on
 * purpose:
 *   - is_empty / is_not_empty — an empty value is exactly what they test for.
 *   - is_one_of — rhs is an array; an empty array already fails safe via
 *     `.some()` on `[]` in operators.ts, no readiness gate needed.
 *   - gt/gte/lt/lte — already fail closed on an empty operand (WEB-1268),
 *     before the Number() coercion that would otherwise read '' as 0.
 * not_equals/not_contains are included alongside equals/contains even though
 * the audit comment names only equals/contains/begins_with/ends_with
 * verbatim — they are the negated form of the exact same comparison and
 * degenerate the same way (compare-against-nothing). Judgment call, not
 * something the audit spelled out (R5).
 */
const OPERATORS_REQUIRING_NONEMPTY_VALUE: ReadonlySet<Condition['operator']> = new Set([
  'equals',
  'not_equals',
  'contains',
  'not_contains',
  'begins_with',
  'ends_with',
]);

/** True if this source is one where a blank source_ref means "not configured". */
export function isSourceRefRequired(source: Condition['source']): boolean {
  return SOURCES_REQUIRING_REF.has(source);
}

/** True if this operator treats an empty literal `value` as a real comparison target. */
export function isConditionValueRequired(operator: Condition['operator']): boolean {
  return OPERATORS_REQUIRING_NONEMPTY_VALUE.has(operator);
}

/**
 * isConditionReady — WEB-1255 audit 22.08 follow-up. The original fix
 * (evaluateGroup below) only closed the case of a condition GROUP with zero
 * items. A single condition that IS present but half-filled degenerates the
 * exact same way: with an empty source_ref, `resolveSource` returns
 * `ctx.*[ '' ]` (undefined); with an empty literal `value`, `equals`/
 * `contains`/`begins_with`/`ends_with` compare against '' — both read as
 * "true for any visitor who doesn't have that source" (operators.ts: norm('')
 * === norm(''), ''.includes(''), etc). A condition is NOT ready when:
 *   (a) its source requires a source_ref and none is set, or
 *   (b) its operator requires a non-empty literal value and the RAW value
 *       (before any {var_ref} / context resolution) is an empty string.
 * {var_ref} values are never "not ready" by this check — the author
 * explicitly picked a variable; what it resolves to at runtime is ordinary
 * evaluation, not a readiness gap. A not-ready condition must never match —
 * used by evaluateCondition below (fail-closed) AND by the editor
 * (ConditionRow) to mark the SAME condition incomplete, so "not ready" is
 * defined in exactly one place for both engine and UI.
 */
export function isConditionReady(condition: Condition): boolean {
  if (isSourceRefRequired(condition.source) && !(condition.source_ref ?? '').trim()) {
    return false;
  }
  if (
    isConditionValueRequired(condition.operator) &&
    typeof condition.value === 'string' &&
    condition.value.trim() === ''
  ) {
    return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// Source resolution
// ---------------------------------------------------------------------------

/**
 * Resolve the left-hand side value from EvalContext based on the condition source.
 * Returns undefined for unknown sources (fail-open path).
 */
function resolveSource(
  condition: Condition,
  ctx: EvalContext
): string | string[] | null | undefined {
  const ref = condition.source_ref ?? '';
  switch (condition.source) {
    case 'ANSWER':
      // D1: answers store option.id (choice blocks) — NOT label
      return ctx.answers[ref];
    case 'URL_PARAM':
      return ctx.urlParams[ref];
    case 'GEO_COUNTRY':
      return ctx.geoCountry;
    case 'DEVICE_OS':
      return ctx.deviceOs;
    case 'BROWSER':
      // source_ref='in_app' targets the boolean in-app-webview flag (spec :198,
      // critical for paid-social); otherwise the browser name itself.
      return condition.source_ref === 'in_app'
        ? String(ctx.browserInApp)
        : ctx.browser;
    case 'PAYMENT_METHOD':
      return ctx.paymentMethod;
    case 'VARIABLE':
      return ctx.variables[ref];
    default:
      // Unknown source → undefined → fail-open (false)
      return undefined;
  }
}

/**
 * Resolve the right-hand side (condition value) to string | string[].
 * {var_ref} is looked up in ctx.variables; missing var → '' (fail-open).
 */
function resolveValue(
  value: Condition['value'],
  ctx: EvalContext
): string | string[] {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value;
  // {var_ref} — look up in ctx.variables
  return ctx.variables[value.var_ref] ?? '';
}

// ---------------------------------------------------------------------------
// evaluateCondition
// ---------------------------------------------------------------------------

/**
 * Evaluate a single Condition against EvalContext.
 * Returns false on any error or unknown source/operator (fail-open).
 */
export function evaluateCondition(cond: Condition, ctx: EvalContext): boolean {
  try {
    // WEB-1255 audit 22.08: a half-filled condition (missing source_ref, or
    // an empty literal value on a string-comparison operator) must never
    // match — fail-closed, same principle as the empty-group fix below.
    if (!isConditionReady(cond)) {
      return false;
    }

    // GEO_COUNTRY=null → decided by the author's explicit degradation_fallback
    // choice (WEB-2174). Absent field → 'false' → same as before this fix:
    // not matched, not blocking. This default MUST NOT change — live funnels
    // built as "show only to country X" rely on it staying fail-open-false.
    // 'true' is the escape hatch for the OPPOSITE intent ("hide for country
    // X"), where an unknown country (e.g. any VPN) must not defeat the rule.
    if (cond.source === 'GEO_COUNTRY' && ctx.geoCountry === null) {
      return cond.degradation_fallback === 'true';
    }

    const lhs = resolveSource(cond, ctx);
    const rhs = resolveValue(cond.value, ctx);

    return applyOperator(cond.operator, lhs, rhs);
  } catch {
    return false; // fail-open
  }
}

// ---------------------------------------------------------------------------
// evaluateGroup
// ---------------------------------------------------------------------------

/**
 * Evaluate a Group recursively.
 *   ALL  → every item must be true (AND semantics)
 *   ANY  → at least one item must be true (OR semantics)
 */
export function evaluateGroup(group: Group, ctx: EvalContext): boolean {
  const { combinator, items } = group;

  // WEB-1255: an empty condition group must NOT be treated as vacuously
  // true. `[].every(...)` returns true in JS, which used to make an
  // unfinished ALL-group match every visitor unconditionally. Made explicit
  // for BOTH combinators so a reader never has to reason about `every`/`some`
  // edge-case semantics on an empty array — a group with zero conditions
  // simply never matches.
  if (items.length === 0) {
    return false;
  }

  if (combinator === 'ALL') {
    return items.every((item) => evaluateItem(item, ctx));
  }

  if (combinator === 'ANY') {
    return items.some((item) => evaluateItem(item, ctx));
  }

  // Unknown combinator → fail-open
  return false;
}

/** Dispatch to evaluateGroup or evaluateCondition based on item shape. */
function evaluateItem(
  item: Condition | Group,
  ctx: EvalContext
): boolean {
  // Groups have a 'combinator' field; Conditions have 'source'
  if ('combinator' in item) {
    return evaluateGroup(item as Group, ctx);
  }
  return evaluateCondition(item as Condition, ctx);
}

// ---------------------------------------------------------------------------
// evaluate (top-level)
// ---------------------------------------------------------------------------

/**
 * Evaluate a full Rule against EvalContext.
 * Returns false for broken/unknown rules (fail-open). Never throws.
 */
export function evaluate(rule: Rule, ctx: EvalContext): boolean {
  try {
    return evaluateGroup(rule.condition_group, ctx);
  } catch {
    return false; // fail-open
  }
}
